// src/pages/ReceiptDetails.jsx
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    List, User, Hash, Calendar, DollarSign, Mail, Printer,
    MoreVertical, ChevronLeft, Search, Menu, X, Loader2, CheckSquare, Square,
    FileText,
    Ban
} from 'lucide-react';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { useToast } from '../hooks/useToast';
import { fetchReceiptForStaff } from '../apiHelper';
import ReceiptPDFTemplate from './ReceiptPDFTemplate';
import Dropdown from '../components/ui/Dropdown';
import FastAPIClient from '../services/apiClient';

const client = new FastAPIClient();

const PrintButton = ({ receipt, loading }) => {
    const componentRef = useRef();

    const handlePrint = () => {
        if (loading || !receipt) return;

        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
      <!DOCTYPE html>
      <html lang="en">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Receipt ${receipt.idempotency_key}</title>
        <script src="https://cdn.tailwindcss.com"></script>
        <link href="https://fonts.googleapis.com/css2?family=Open+Sans:wght@400;600;700&display=swap" rel="stylesheet">
        <style>
          body { margin: 0; padding: 0; background: white; }
          @page { size: A4; margin: 0; }
          @media print {
            body { -webkit-print-color-adjust: exact; color-adjust: exact; }
            .print-container { padding: 40px; }
          }
          body, * { font-family: 'Open Sans', system-ui, sans-serif !important; }
        </style>
      </head>
      <body class="print-container bg-white">
        <div class="max-w-[210mm] mx-auto bg-white min-h-[297mm]">
          ${componentRef.current.innerHTML}
        </div>
      </body>
      </html>
    `);
        printWindow.document.close();
        printWindow.focus();
        printWindow.onload = () => {
            setTimeout(() => {
                printWindow.print();
                printWindow.close();
            }, 300);
        };
    };

    return (
        <>
            <div className="hidden">
                <div ref={componentRef}>
                    <ReceiptPDFTemplate receipt={receipt} loading={false} />
                </div>
            </div>
            <Button
                variant="secondary"
                className="flex items-center gap-2"
                onClick={handlePrint}
                disabled={loading || !receipt}
            >
                <Printer size={18} />
                Print Receipt
            </Button>
        </>
    );
};

const ReceiptDetails = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { showToast } = useToast();

    const listRef = useRef(null);
    const { selectedReceipt, allReceipts = [] } = location.state || {};

    const [activeReceiptId, setActiveReceiptId] = useState(selectedReceipt?.orders_id);
    const [searchTerm, setSearchTerm] = useState('');
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
    const [user, setUser] = useState(null);
    const [detailedReceipt, setDetailedReceipt] = useState(null);
    const [loadingReceipt, setLoadingReceipt] = useState(false);

    // Load user
    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (!lcUser) {
            showToast({ message: "Please login to view receipts.", show: true, toastType: 0 });
            navigate("/login");
            return;
        }
        try {
            const parsedUser = JSON.parse(lcUser);
            setUser(parsedUser);
        } catch {
            showToast({ message: "Invalid session.", show: true, toastType: 2 });
            navigate("/login");
        }
    }, [showToast, navigate]);

    const currentReceipt = useMemo(() => {
        return allReceipts.find(r => r.orders_id === activeReceiptId) || selectedReceipt;
    }, [allReceipts, activeReceiptId, selectedReceipt]);

    // Fetch full receipt with line items
    useEffect(() => {
        if (!currentReceipt?.orders_id || !user?.users_id) return;

        const loadReceipt = async () => {
            setLoadingReceipt(true);
            try {
                const data = await fetchReceiptForStaff({
                    users_id: user.users_id,
                    orders_id: currentReceipt.orders_id
                });
                setDetailedReceipt(data);
            } catch (err) {
                console.error(err);
                showToast({ message: "Failed to load receipt details", show: true, toastType: 2 });
            } finally {
                setLoadingReceipt(false);
            }
        };
        loadReceipt();
    }, [currentReceipt?.orders_id, user?.users_id, showToast]);

    const filteredReceipts = useMemo(() => {
        if (!searchTerm.trim()) return allReceipts;
        const lower = searchTerm.toLowerCase();
        return allReceipts.filter(r =>
            r.customer.name.toLowerCase().includes(lower) ||
            r.idempotency_key.toLowerCase().includes(lower)
        );
    }, [allReceipts, searchTerm]);

    const handleVoidReceipt = useCallback(async () => {
        if (!detailedReceipt) {
            // showToast({ message: "Receipt details not loaded yet.", show: true, toastType: 1 });
            showToast("Receipt details not loaded yet.", "error");
            return;
        }
        
        const payload = {
            orderId: detailedReceipt.orders_id,
            receiptNumber: detailedReceipt.idempotency_key,
            customer_id: detailedReceipt.customer.member_id,
            storeId: detailedReceipt.store.store_id
        }

        try {
            const response = await client.postData("/sales/void-receipt", payload)
            // console.log(response)
            showToast(response.msg)
        } catch (err) {
            const errorMessage = err.response?.data?.detail || `Failed to void receipt.`
            showToast(errorMessage, "error")
        }
        // showToast("Receipt logged to console (Void initiated).", "info");
    }, [detailedReceipt, showToast]);

    const handleReceiptSelect = useCallback((id) => {
        setActiveReceiptId(id);
        setMobileSidebarOpen(false);
    }, []);

    useEffect(() => {
        if (activeReceiptId && listRef.current) {
            const el = listRef.current.querySelector(`[data-id="${activeReceiptId}"]`);
            el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }, [activeReceiptId]);

    const formatDate = (dateStr) => {
        return dateStr ? new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';
    };

    if (!allReceipts.length || !currentReceipt) {
        return (
            <div className='flex flex-col items-center justify-center min-h-[60vh] text-center p-8 text-orange-600'>
                <p className="text-lg mb-4">No receipt data available.</p>
                <Button onClick={() => navigate('/sales/receipts')} variant="primary">
                    <ChevronLeft size={16} className="mr-2" /> Back to Receipts
                </Button>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-[calc(100vh-100px)] bg-gray-100">
            {/* Mobile Header */}
            <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between sticky top-0 z-30">
                <Button variant="ghost" size="sm" onClick={() => navigate('/sales/receipts')}>
                    <ChevronLeft size={20} />
                </Button>
                <h1 className="text-lg font-bold text-text-primary truncate px-4">
                    Receipt {currentReceipt.idempotency_key}
                </h1>
                <Button variant="ghost" size="sm" onClick={() => setMobileSidebarOpen(true)}>
                    <Menu size={22} />
                </Button>
            </div>

            <div className="flex flex-1 overflow-hidden">
                {/* Mobile Sidebar */}
                <div className={`fixed inset-0 z-40 lg:hidden transition-transform duration-300 ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
                    <div className="absolute inset-0 bg-black bg-opacity-50" onClick={() => setMobileSidebarOpen(false)} />
                    <div className="absolute left-0 top-0 h-full w-full max-w-sm bg-white shadow-2xl flex flex-col">
                        <div className="flex justify-between items-center p-4 border-b border-gray-200">
                            <h2 className="text-lg font-bold flex items-center gap-2">
                                <List size={20} /> Receipts ({allReceipts.length})
                            </h2>
                            <Button variant="ghost" size="sm" onClick={() => setMobileSidebarOpen(false)}>
                                <X size={20} />
                            </Button>
                        </div>
                        <div className="p-4 border-b border-gray-200">
                            <Input
                                type="text"
                                placeholder="Search receipts..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 pr-4 py-2 text-sm"
                                icon={<Search size={18} className="text-gray-dark absolute left-3 top-1/2 -translate-y-1/2" />}
                            />
                        </div>
                        <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-3">
                            {filteredReceipts.map(r => (
                                <div
                                    key={r.orders_id}
                                    data-id={r.orders_id}
                                    onClick={() => handleReceiptSelect(r.orders_id)}
                                    className={`p-4 rounded-lg cursor-pointer transition-all border ${r.orders_id === activeReceiptId
                                        ? 'bg-gmail-blue text-white border-gmail-blue shadow-md'
                                        : 'hover:bg-gray-50 border-gray-200'
                                        }`}
                                >
                                    <div className="flex items-center gap-3 mb-2">
                                        {r.orders_id === activeReceiptId ? <CheckSquare size={16} /> : <Square size={14} className="text-gray-400" />}
                                        <div>
                                            <p className={`font-bold text-sm ${r.orders_id === activeReceiptId ? 'text-white' : 'text-text-primary'}`}>
                                                {r.customer.name}
                                            </p>
                                            <p className={`text-xs ${r.orders_id === activeReceiptId ? 'text-gray-100' : 'text-text-secondary'}`}>
                                                {r.idempotency_key}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3 text-xs mt-3">
                                        <div>
                                            <p className="text-secondary">Amount</p>
                                            <p className={`font-bold ${r.orders_id === activeReceiptId ? 'text-white' : ''}`}>
                                                {r.total_amount_formatted}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-secondary">Date</p>
                                            <p className={r.orders_id === activeReceiptId ? 'text-white' : ''}>
                                                {formatDate(r.placed_on)}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Desktop Sidebar */}
                <div className="hidden lg:block w-full lg:w-1/3 xl:w-1/4">
                    <Card className="h-full bg-white rounded-lg shadow-md flex flex-col">
                        <div className="flex justify-between items-center p-4 border-b border-gray-200">
                            <h2 className="text-xl font-bold flex items-center gap-2">
                                <List size={20} className="text-gmail-blue" /> All Receipts ({allReceipts.length})
                            </h2>
                            <Button variant="ghost" size="sm" onClick={() => navigate('/sales/receipts')}>
                                <ChevronLeft size={20} />
                            </Button>
                        </div>
                        <div className="p-4">
                            <Input
                                type="text"
                                placeholder="Search receipts..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 pr-4 py-2 text-sm"
                                icon={<Search size={18} className="text-gray-dark absolute left-3 top-1/2 -translate-y-1/2" />}
                            />
                        </div>
                        <div ref={listRef} className="flex-1 overflow-y-auto space-y-2 px-4 pb-4">
                            {filteredReceipts.map(r => (
                                <div
                                    key={r.orders_id}
                                    data-id={r.orders_id}
                                    onClick={() => handleReceiptSelect(r.orders_id)}
                                    className={`p-4 rounded-lg cursor-pointer transition-all border ${r.orders_id === activeReceiptId
                                        ? 'bg-gmail-blue text-white border-gmail-blue shadow-md'
                                        : 'hover:bg-gray-50 border-gray-200'
                                        }`}
                                >
                                    <div className="flex items-center gap-3 mb-2">
                                        {r.orders_id === activeReceiptId ? <CheckSquare size={16} /> : <Square size={14} className="text-gray-400" />}
                                        <div>
                                            <p className={`font-bold text-sm ${r.orders_id === activeReceiptId ? 'text-white' : 'text-text-primary'}`}>
                                                {r.customer.name}
                                            </p>
                                            <p className={`text-xs ${r.orders_id === activeReceiptId ? 'text-gray-100' : 'text-text-secondary'}`}>
                                                {r.idempotency_key}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3 text-xs mt-3">
                                        <div><p className="text-secondary">Amount</p><p className={`font-bold ${r.orders_id === activeReceiptId ? 'text-white' : ''}`}>{r.total_amount_formatted}</p></div>
                                        <div><p className="text-secondary">Date</p><p className={r.orders_id === activeReceiptId ? 'text-white' : ''}>{formatDate(r.placed_on)}</p></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Card>
                </div>

                {/* Main Detail Pane */}
                <div className="flex-1 bg-white rounded-lg shadow-md flex flex-col overflow-hidden">
                    <div className="p-6 border-b border-gray-200">
                        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
                            <div>
                                <h1 className="text-2xl lg:text-3xl font-bold text-text-primary flex items-center gap-3">
                                    <Hash size={28} className="text-gmail-blue" />
                                    {currentReceipt.idempotency_key}
                                </h1>
                                <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-text-secondary">
                                    <div className="flex items-center gap-2">
                                        <User size={16} /> <span className="font-medium text-text-primary">{currentReceipt.customer.name}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Calendar size={16} /> {formatDate(currentReceipt.placed_on)}
                                    </div>
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <Button variant="primary" className="flex items-center gap-2">
                                    <Mail size={18} /> Send Email
                                </Button>
                                <PrintButton receipt={detailedReceipt} loading={loadingReceipt} />
                                <Dropdown
                                    trigger={<Button variant="ghost" size="sm" className="p-2"><MoreVertical size={20} /></Button>}
                                    align="right"
                                >
                                    <button className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-100 flex items-center gap-3"><FileText size={16} />View Journal </button>
                                    <hr className="my-1 border-gray-200" />
                                    <button 
                                        onClick={handleVoidReceipt}
                                        className="w-full text-left px-4 py-2.5 text-sm hover:bg-red-50 text-red-600 flex items-center gap-3">
                                            <Ban size={16} />Void Receipt 
                                    </button>
                                </Dropdown>
                            </div>
                        </div>
                        <div className="flex justify-between items-center mt-6">
                            <span className="inline-block px-5 py-2 rounded-full text-sm font-bold bg-green-100 text-green-800">
                                Completed
                            </span>
                            <div className="text-right">
                                <p className="text-sm text-text-secondary">Total Paid</p>
                                <p className="text-2xl lg:text-3xl font-bold text-gmail-blue">
                                    {currentReceipt.total_amount_formatted}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto bg-gray-50 p-4 lg:p-8">
                        <div className="max-w-4xl mx-auto">
                            <ReceiptPDFTemplate receipt={detailedReceipt} loading={loadingReceipt} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReceiptDetails;